import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { getCategorySlug, getMovieById, heroSlides } from "@/lib/movieData";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback((next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const handleTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 48) goTo(index + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点影片推荐"
    >
      <div className="relative w-full h-[520px] md:h-[600px] lg:h-[640px]">
        {heroSlides.map((slide, slideIndex) => {
          const movie = getMovieById(slide.movieId);
          if (!movie) return null;
          return (
            <div
              key={slide.movieId}
              className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
                slideIndex === index ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            >
              <img
                src={slide.image}
                alt={`《${movie.title}》焦点海报，${movie.year}年${movie.region}${movie.types.join("、")}电影`}
                title={`${movie.title} - 秋霞电影`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/25"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/10 to-primary-950/40"></div>
            </div>
          );
        })}

        <div className="relative z-10 h-full w-full px-4 md:px-6 flex items-center">
          <div className="w-full max-w-[1180px] mx-auto">
            {heroSlides.map((slide, slideIndex) => {
              const movie = getMovieById(slide.movieId);
              if (!movie) return null;
              return (
                <div
                  key={slide.movieId}
                  className={`max-w-[640px] transition-all duration-700 ${
                    slideIndex === index
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-6 pointer-events-none absolute"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                      <i className="ri-flashlight-fill text-[11px]"></i>
                      {slide.tag}
                    </span>
                    <span className="text-xs tracking-[0.25em] text-background-200/80">
                      {movie.year} · {movie.region}
                    </span>
                  </div>

                  <h2 className="mt-5 font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-background-50 leading-[1.15]">
                    {movie.title}
                  </h2>

                  <p className="mt-3 text-sm md:text-base text-background-200/85">
                    {movie.types.join(" / ")} · 导演 {movie.director} · 片长 {movie.duration} 分钟
                  </p>

                  <p className="mt-4 max-w-[520px] text-sm md:text-base leading-relaxed text-background-100/80 line-clamp-3">
                    {movie.summary} {movie.synopsis.slice(0, 60)}…
                  </p>

                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${movie.id}`}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      查看影片资料
                    </Link>
                    <Link
                      to={`/category/${getCategorySlug(movie.types[0])}`}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/40 text-background-50 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-layout-grid-line text-base"></i>
                      同类影片
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="absolute z-20 right-4 md:right-6 bottom-6 hidden md:flex flex-col items-end gap-2">
          {heroSlides.map((slide, slideIndex) => {
            const movie = getMovieById(slide.movieId);
            return (
              <button
                key={slide.movieId}
                type="button"
                onClick={() => goTo(slideIndex)}
                className={`group flex items-center gap-3 cursor-pointer transition-colors ${
                  slideIndex === index ? "text-background-50" : "text-background-200/50 hover:text-background-100"
                }`}
              >
                <span className="text-[11px] tracking-widest">
                  {String(slideIndex + 1).padStart(2, "0")}
                </span>
                <span
                  className={`block h-[2px] transition-all duration-300 ${
                    slideIndex === index ? "w-12 bg-accent-500" : "w-6 bg-background-50/30 group-hover:w-9"
                  }`}
                ></span>
                <span className="text-xs max-w-[100px] truncate">{movie?.title}</span>
              </button>
            );
          })}
        </div>

        <div className="absolute z-20 left-4 md:left-6 bottom-6 flex md:hidden items-center gap-1.5">
          {heroSlides.map((slide, slideIndex) => (
            <button
              key={slide.movieId}
              type="button"
              aria-label={`切换到第 ${slideIndex + 1} 张`}
              onClick={() => goTo(slideIndex)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                slideIndex === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/40"
              }`}
            ></button>
          ))}
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="absolute z-20 left-3 md:left-6 top-1/2 -translate-y-1/2 hidden md:flex w-10 h-10 items-center justify-center rounded-full border border-background-50/25 text-background-50/80 hover:bg-background-50/15 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="absolute z-20 right-3 md:right-6 top-1/2 -translate-y-1/2 hidden md:flex w-10 h-10 items-center justify-center rounded-full border border-background-50/25 text-background-50/80 hover:bg-background-50/15 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}